import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import MobileCardSlider from './MobileCardSlider';
import { 
  Brain, 
  Trophy, 
  Zap, 
  Clock, 
  Star,
  Play,
  CheckCircle,
  X,
  Award,
  ArrowLeft,
  Home
} from 'lucide-react';

const SampleQuizzesSection = () => {
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(15);
  const [answered, setAnswered] = useState(false);
  const [consecutiveCorrect, setConsecutiveCorrect] = useState(0);
  const [lastResult, setLastResult] = useState(null);

  // Timer effect for demo quiz
  useEffect(() => {
    if (selectedQuiz && !answered && !quizCompleted && timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && !answered) {
      // Auto skip when time runs out
      handleSkipQuestion(true);
    }
  }, [timeLeft, answered, quizCompleted, selectedQuiz]);

  const sampleQuizzes = [
    {
      id: 1,
      title: 'General Knowledge Challenge',
      description: 'Test your GK with bilingual questions!',
      icon: Brain,
      difficulty: 'Medium',
      questions: 5,
      timeLimit: '1 min',
      category: 'General Knowledge',
      color: 'from-blue-500 to-purple-600',
      questions_data: [
        {
          question_english: "Which city is the capital of Maharashtra?",
          question_hindi: "महाराष्ट्र की राजधानी कौन सा शहर है?",
          options: ["A) Mumbai / मुंबई", "B) Pune / पुणे", "C) Nagpur / नागपुर", "D) Nashik / नाशिक"],
          correct: 0
        },
        {
          question_english: "What is the largest desert in the world?",
          question_hindi: "दुनिया का सबसे बड़ा रेगिस्तान कौन सा है?",
          options: ["A) Sahara / सहारा", "B) Gobi / गोबी", "C) Antarctic Desert / अंटार्कटिक रेगिस्तान", "D) Thar / थार"],
          correct: 2
        },
        {
          question_english: "What is the national bird of India?",
          question_hindi: "भारत का राष्ट्रीय पक्षी क्या है?",
          options: ["A) Peacock / मोर", "B) Sparrow / गौरैया", "C) Parrot / तोता", "D) Eagle / चील"],
          correct: 0
        },
        {
          question_english: "Who was the first Indian to win a Nobel Prize?",
          question_hindi: "नोबेल पुरस्कार जीतने वाले पहले भारतीय कौन थे?",
          options: ["A) Rabindranath Tagore / रवींद्रनाथ टैगोर", "B) C.V. Raman / सी.वी. रमन", "C) Amartya Sen / अमर्त्य सेन", "D) Mother Teresa / मदर टेरेसा"],
          correct: 0
        },
        {
          question_english: "What is the chemical symbol for water?",
          question_hindi: "पानी का रासायनिक प्रतीक क्या है?",
          options: ["A) H2O / एच2ओ", "B) CO2 / सीओ2", "C) O2 / ओ2", "D) NaCl / नैक्ल"],
          correct: 0
        }
      ]
    },
    {
      id: 2,
      title: 'Advanced Knowledge Quiz',
      description: 'Challenge yourself with harder questions!',
      icon: Zap,
      difficulty: 'Hard',
      questions: 4,
      timeLimit: '2 min',
      category: 'Advanced Knowledge',
      color: 'from-green-500 to-teal-600',
      questions_data: [
        {
          question_english: "What is the name of the process by which water moves through a plant?",
          question_hindi: "पौधे के माध्यम से पानी की गति की प्रक्रिया का नाम क्या है?",
          options: ["A) Respiration / श्वसन", "B) Transpiration / वाष्पोत्सर्जन", "C) Photosynthesis / प्रकाश संश्लेषण", "D) Osmosis / परासरण"],
          correct: 1
        },
        {
          question_english: "Who is credited with developing the theory of relativity?",
          question_hindi: "सापेक्षता के सिद्धांत को विकसित करने का श्रेय किसे दिया जाता है?",
          options: ["A) Albert Einstein / अल्बर्ट आइंस्टीन", "B) Isaac Newton / आइज़क न्यूटन", "C) Galileo Galilei / गैलीलियो गैलिली", "D) Stephen Hawking / स्टीफन हॉकिंग"],
          correct: 0
        },
        {
          question_english: "Who discovered the neutron?",
          question_hindi: "न्यूट्रॉन की खोज किसने की?",
          options: ["A) Ernest Rutherford / अर्नेस्ट रदरफोर्ड", "B) James Chadwick / जेम्स चैडविक", "C) Niels Bohr / नील्स बोहर", "D) Enrico Fermi / एनरिको फर्मी"],
          correct: 1
        },
        {
          question_english: "What is the name of the Indian satellite series used for communication?",
          question_hindi: "संचार के लिए उपयोग की जाने वाली भारतीय उपग्रह श्रृंखला का नाम क्या है?",
          options: ["A) INSAT / इनसैट", "B) IRS / आईआरएस", "C) EDUSAT / एडूसैट", "D) CARTOSAT / कार्टोसैट"],
          correct: 0
        }
      ]
    },
    {
      id: 3,
      title: 'Mixed Knowledge Special',
      description: 'Variety questions from different topics!',
      icon: Trophy,
      difficulty: 'Easy',
      questions: 4,
      timeLimit: '90 sec',
      category: 'Mixed Topics',
      color: 'from-red-500 to-pink-600',
      questions_data: [
        {
          question_english: "Which is the smallest state in India by area?",
          question_hindi: "भारत का क्षेत्रफल के अनुसार सबसे छोटा राज्य कौन सा है?",
          options: ["A) Goa / गोवा", "B) Sikkim / सिक्किम", "C) Tripura / त्रिपुरा", "D) Mizoram / मिजोरम"],
          correct: 0
        },
        {
          question_english: "What is the capital of Karnataka?",
          question_hindi: "कर्नाटक की राजधानी क्या है?",
          options: ["A) Bengaluru / बेंगलुरु", "B) Mysuru / मैसूर", "C) Mangaluru / मंगलुरु", "D) Hubli / हुबली"],
          correct: 0
        },
        {
          question_english: "Which Indian festival is known as the festival of colors?",
          question_hindi: "कौन सा भारतीय त्योहार रंगों का त्योहार कहलाता है?",
          options: ["A) Holi / होली", "B) Diwali / दिवाली", "C) Dussehra / दशहरा", "D) Navratri / नवरात्रि"],
          correct: 0
        },
        {
          question_english: "Who is known as the 'Missile Man of India'?",
          question_hindi: "भारत के 'मिसाइल मैन' के रूप में किसे जाना जाता है?",
          options: ["A) A.P.J. Abdul Kalam / ए.पी.जे. अब्दुल कलाम", "B) Vikram Sarabhai / विक्रम साराभाई", "C) Homi Bhabha / होमी भाभा", "D) Satish Dhawan / सतीश धवन"],
          correct: 0
        }
      ]
    }
  ];

  const startQuiz = (quiz) => {
    setSelectedQuiz(quiz);
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setQuizCompleted(false);
    setScore(0);
    setTimeLeft(15);
    setAnswered(false);
    setConsecutiveCorrect(0);
    setLastResult(null);
  };

  const selectAnswer = (answerIndex) => {
    if (!answered) {
      setSelectedAnswer(answerIndex);
    }
  };

  const handleSkipQuestion = (isAutoSkip = false) => {
    if (answered) return;
    
    setAnswered(true);
    const timeTaken = 15 - timeLeft;
    
    // Apply -2 points penalty for skipping
    const penaltyPoints = -2;
    const newScore = Math.max(0, score + penaltyPoints);
    setScore(newScore);
    setConsecutiveCorrect(0); // Reset streak when skipping
    
    setLastResult({
      is_correct: false,
      points_earned: penaltyPoints,
      total_score: newScore,
      is_skipped: true,
      skip_type: isAutoSkip ? "auto" : "manual",
      time_taken: timeTaken
    });
    
    // Move to next question after 2 seconds
    setTimeout(() => {
      nextQuestion();
    }, 2000);
  };

  const submitAnswer = () => {
    if (answered || selectedAnswer === null) return;
    
    setAnswered(true);
    const timeTaken = 15 - timeLeft;
    const isCorrect = selectedAnswer === selectedQuiz.questions_data[currentQuestion].correct;
    
    let points = 0;
    let bonusBreakdown = [];
    
    if (isCorrect) {
      // Base points for correct answer
      points = 20;
      bonusBreakdown.push("Correct Answer: +20");
      
      // Time bonus (remaining seconds)
      const timeBonus = Math.max(0, timeLeft);
      points += timeBonus;
      if (timeBonus > 0) {
        bonusBreakdown.push(`Time Bonus: +${timeBonus}`);
      }
      
      const newConsecutive = consecutiveCorrect + 1;
      setConsecutiveCorrect(newConsecutive);
      
      // Streak bonuses
      if (newConsecutive === 3) {
        points += 10;
        bonusBreakdown.push("3-Streak Bonus: +10");
      } else if (newConsecutive === 5) {
        points += 20;
        bonusBreakdown.push("5-Streak Bonus: +20");
      } else if (newConsecutive === 10) {
        points += 50;
        bonusBreakdown.push("10-Streak Bonus: +50");
      }
    } else {
      // Wrong answer penalty
      points = -4;
      bonusBreakdown.push("Wrong Answer: -4");
      setConsecutiveCorrect(0);
    }
    
    const newTotalScore = Math.max(0, score + points);
    setScore(newTotalScore);
    
    setLastResult({
      is_correct: isCorrect,
      points_earned: points,
      total_score: newTotalScore,
      consecutive_correct: isCorrect ? consecutiveCorrect + 1 : 0,
      bonus_breakdown: bonusBreakdown,
      time_taken: timeTaken,
      is_skipped: false
    });
    
    // Move to next question after 2 seconds
    setTimeout(() => {
      nextQuestion();
    }, 2000);
  };

  const nextQuestion = () => {
    if (currentQuestion + 1 < selectedQuiz.questions_data.length) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setAnswered(false);
      setTimeLeft(15);
      setLastResult(null);
    } else {
      setQuizCompleted(true);
    }
  };

  const closeQuiz = () => {
    setSelectedQuiz(null);
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setQuizCompleted(false);
    setScore(0);
  };

  const handleRegisterNow = () => {
    const demoComplete = {
      timestamp: new Date().toISOString(),
      action: 'demo_completed_register',
      quiz: selectedQuiz.title,
      score: score
    };
    localStorage.setItem('winquizz_demo_complete', JSON.stringify(demoComplete));
    
    // Navigate to dashboard instead of showing alert
    window.location.href = '/dashboard';
  };

  const QuizCard = ({ quiz }) => {
    const IconComponent = quiz.icon;
    return (
      <Card className="border-2 border-gray-200 hover:border-red-300 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1 group h-full overflow-visible">
        <CardHeader className="text-center pb-4">
          <div className={`bg-gradient-to-br ${quiz.color} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
            <IconComponent className="h-8 w-8 text-white" />
          </div>
          <CardTitle className="text-lg text-gray-900 mb-2">{quiz.title}</CardTitle>
          <p className="text-gray-600 text-sm mb-2">{quiz.description}</p>
          <div className="flex justify-center gap-2">
            <Badge variant="outline" className="text-xs">
              {quiz.difficulty}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {quiz.questions} Qs
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="text-center space-y-3 pt-0 pb-6">
          <div className="flex justify-between text-sm text-gray-600 mb-3">
            <span>⏱️ {quiz.timeLimit}</span>
            <span>📚 {quiz.category}</span>
          </div>
          <Button 
            onClick={() => startQuiz(quiz)}
            className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold transition-all duration-300 transform hover:scale-105"
            size="sm"
          >
            <Play className="mr-2 h-4 w-4" />
            Try Demo Quiz
          </Button>
        </CardContent>
      </Card>
    );
  };

  return (
    <section className="py-16 bg-gradient-to-br from-gray-50 to-gray-100 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Try Sample Quizzes</h2>
          <p className="text-xl text-gray-600">Get a taste of our exciting quiz challenges before you play!</p>
          <Badge className="mt-4 bg-green-100 text-green-800 px-4 py-2">
            🎮 Free Demo - No Registration Required
          </Badge>
        </div>

        {!selectedQuiz ? (
          <>
            {/* Mobile Card Slider with improved responsive design */}
            <div className="py-6">
              <MobileCardSlider 
                showNavigation={true} 
                showDots={true}
                autoSlide={false}
              >
                {sampleQuizzes.map((quiz) => (
                  <div key={quiz.id} className="px-2 py-4">
                    <QuizCard quiz={quiz} />
                  </div>
                ))}
              </MobileCardSlider>
            </div>

            {/* Register CTA - More mobile-friendly */}
            <div className="text-center mt-12">
              <div className="bg-red-50 rounded-xl p-6 max-w-3xl mx-auto border border-red-200">
                <h3 className="text-xl md:text-2xl font-bold text-red-800 mb-4">Ready for Real Contests?</h3>
                <p className="text-red-700 mb-6 text-sm md:text-base">
                  These are just demo quizzes! Register now to play real contests with amazing prizes worth lakhs of rupees.
                </p>
                <Button 
                  onClick={() => {
                    const demoInterest = {
                      timestamp: new Date().toISOString(),
                      action: 'post_demo_register_interest'
                    };
                    localStorage.setItem('winquizz_post_demo', JSON.stringify(demoInterest));
                    window.location.href = '/dashboard';
                  }}
                  size="lg" 
                  className="bg-red-600 hover:bg-red-700 text-white px-6 md:px-8 py-3 md:py-4 text-base md:text-lg font-semibold w-full sm:w-auto"
                >
                  <Trophy className="mr-2 h-5 w-5" />
                  Register & Win Real Prizes
                </Button>
              </div>
            </div>
          </>
        ) : (
          /* Quiz Modal - Fixed sizing and improved mobile responsiveness */
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg mx-auto max-h-[95vh] overflow-y-auto">
              <div className="bg-red-50 border-b p-4 sticky top-0">
                <div className="flex justify-between items-center mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg md:text-xl font-bold text-red-800">{selectedQuiz.title}</h3>
                    <p className="text-red-600 text-sm">Demo - Q{currentQuestion + 1}/{selectedQuiz.questions_data.length}</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-center">
                      <div className="text-lg font-bold text-red-600">{timeLeft}s</div>
                      <div className="text-xs text-red-700">Time Left</div>
                    </div>
                    <Button variant="outline" size="sm" onClick={closeQuiz}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm text-red-700">
                  <span className="flex items-center"><Clock className="h-4 w-4 mr-1" />Demo Mode</span>
                  <span className="flex items-center"><Trophy className="h-4 w-4 mr-1" />Score: {score} points</span>
                  {consecutiveCorrect > 0 && (
                    <span className="flex items-center text-green-600">🔥 {consecutiveCorrect} streak</span>
                  )}
                </div>
              </div>
              
              <div className="p-4 md:p-6">
                {!quizCompleted ? (
                  <div>
                    <div className="mb-4">
                      <h4 className="text-base md:text-lg font-semibold text-gray-900 mb-2">
                        {selectedQuiz.questions_data[currentQuestion].question_english}
                      </h4>
                      <p className="text-sm md:text-base text-gray-600 mb-4">
                        {selectedQuiz.questions_data[currentQuestion].question_hindi}
                      </p>
                    </div>
                    <div className="space-y-3 mb-4">
                      {selectedQuiz.questions_data[currentQuestion].options.map((option, index) => (
                        <button
                          key={index}
                          onClick={() => selectAnswer(index)}
                          disabled={answered}
                          className={`w-full p-3 text-left border-2 rounded-lg transition-all duration-300 text-sm md:text-base ${
                            answered
                              ? (index === selectedQuiz.questions_data[currentQuestion].correct
                                  ? 'border-green-500 bg-green-50 text-green-800'
                                  : selectedAnswer === index
                                    ? 'border-red-500 bg-red-50 text-red-800'
                                    : 'border-gray-200 bg-gray-50 text-gray-600')
                              : selectedAnswer === index
                                ? 'border-red-500 bg-red-50 text-red-800'
                                : 'border-gray-200 hover:border-red-300 hover:bg-red-50'
                          }`}
                        >
                          {option}
                        </button>
                      ))}
                    </div>

                    {/* Answer Result */}
                    {lastResult && (
                      <div className={`mb-4 p-4 rounded-lg border ${
                        lastResult.is_skipped 
                          ? 'bg-yellow-50 border-yellow-200'
                          : lastResult.is_correct 
                            ? 'bg-green-50 border-green-200'
                            : 'bg-red-50 border-red-200'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <div className={`text-sm font-semibold ${
                              lastResult.is_skipped 
                                ? 'text-yellow-600'
                                : lastResult.is_correct 
                                  ? 'text-green-600' 
                                  : 'text-red-600'
                            }`}>
                              {lastResult.is_skipped 
                                ? `⏭️ Question ${lastResult.skip_type === 'auto' ? 'Auto-' : ''}Skipped`
                                : lastResult.is_correct 
                                  ? '✅ Correct!' 
                                  : '❌ Wrong Answer'}
                            </div>
                            <div className="text-xs text-gray-600">
                              Points: {lastResult.points_earned > 0 ? '+' : ''}{lastResult.points_earned}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-bold text-gray-900">Total: {lastResult.total_score}</div>
                            {lastResult.consecutive_correct > 0 && (
                              <div className="text-xs text-green-600">🔥 {lastResult.consecutive_correct} streak!</div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex flex-col space-y-3">
                      <Button
                        onClick={submitAnswer}
                        disabled={selectedAnswer === null || answered}
                        className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-300"
                      >
                        {currentQuestion + 1 === selectedQuiz.questions_data.length ? 'Finish Quiz' : 'Submit Answer'}
                      </Button>
                      
                      {!answered && (
                        <Button
                          onClick={() => handleSkipQuestion(false)}
                          variant="outline"
                          className="w-full bg-yellow-50 text-yellow-700 border-yellow-300 hover:bg-yellow-100"
                        >
                          ⏭️ Skip Question (-2 points)
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="mb-6">
                      <Award className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
                      <h4 className="text-lg md:text-xl font-bold text-gray-900 mb-2">Demo Quiz Complete!</h4>
                      <p className="text-base md:text-lg text-gray-600">Your Score: <span className="font-bold text-red-600">{score} points</span></p>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                      <p className="text-yellow-800 text-sm">
                        <strong>🎯 Great job!</strong> In real contests, you compete for amazing prizes like residential plots and smartwatches!
                      </p>
                    </div>
                    {/* Fixed button layout for mobile */}
                    <div className="flex flex-col gap-3">
                      <Button 
                        onClick={handleRegisterNow} 
                        className="w-full bg-red-600 hover:bg-red-700 text-white order-1"
                      >
                        <Trophy className="mr-2 h-4 w-4" />
                        Register to Play Real Contests
                      </Button>
                      <Button 
                        onClick={closeQuiz} 
                        variant="outline" 
                        className="w-full order-2"
                      >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Try Another Quiz
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default SampleQuizzesSection;